import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { storeLink } from "./StoreButtons";
import { Reveal, SectionLabel } from "./Reveal";

const PLACEHOLDERS = Array.from({ length: 9 }, (_, i) => ({ id: `ph-${i}`, name: "[Nama pengguna]", rating: 5, comment: "[Komentar dari Google Play]" }));

export const Stars = ({ value, size = "h-4 w-4" }) => (
  <div className="flex gap-0.5" aria-label={`${value} dari 5 bintang`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`${size} ${n <= value ? "fill-volt text-volt" : "text-zinc-600"}`} />
    ))}
  </div>
);

const Card = ({ t, i }) => (
  <Reveal delay={(i % 3) * 0.08} className="h-full w-[84%] shrink-0 snap-center md:w-auto">
    <motion.figure whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 260, damping: 22 }} data-testid={`testimonial-card-${i}`} className="tile relative flex h-full flex-col rounded-3xl bg-surface p-7 ring-1 ring-white/[0.07]">
      <Stars value={t.rating} />
      <blockquote className="mt-5 flex-1 text-base leading-relaxed text-zinc-200">“{t.comment}”</blockquote>
      <figcaption className="mt-7 flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-volt font-display text-sm font-bold text-ink">{(t.name || "?").replace(/[[\]]/g, "").charAt(0).toUpperCase()}</span>
        <span>
          <span className="block font-semibold text-paper">{t.name}</span>
          <span className="block font-mono text-[12px] text-zinc-400">Ulasan Google Play</span>
        </span>
      </figcaption>
    </motion.figure>
  </Reveal>
);

export const Testimonials = ({ settings }) => {
  const playstore = storeLink(settings, "playstore");
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!process.env.REACT_APP_BACKEND_URL) return undefined;
    api.get("/testimonials").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
    return undefined;
  }, []);
  const list = items?.length ? items.slice(0, 9) : PLACEHOLDERS;
  const avg = items?.length ? (items.reduce((s, t) => s + (t.rating || 0), 0) / items.length).toFixed(1) : null;
  return (
    <section id="testimoni" data-testid="testimonials-section" className="relative bg-surface/40 py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <SectionLabel index="02">Testimoni</SectionLabel>
            <h2 className="mt-6 font-display text-4xl font-bold leading-[1] tracking-tight text-paper md:text-6xl">Kata mereka di <span className="text-volt">Play Store.</span></h2>
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col items-start gap-3 md:items-end">
            {avg ? (
              <div className="flex items-center gap-3" data-testid="testimonials-average">
                <span className="font-display text-4xl font-bold text-volt">{avg}</span>
                <Stars value={Math.round(Number(avg))} size="h-5 w-5" />
              </div>
            ) : null}
            {playstore ? (
            <a data-testid="testimonials-playstore-link" href={playstore} target="_blank" rel="noreferrer" className="group inline-flex items-center gap-1.5 text-sm font-semibold text-paper hover:text-volt">
              Lihat semua ulasan di Google Play <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </a>
            ) : null}
          </Reveal>
        </div>
        <div data-testid="testimonials-track" className="no-scrollbar -mx-5 mt-14 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 md:mx-0 md:grid md:grid-cols-2 md:overflow-visible md:px-0 md:pb-0 lg:grid-cols-3">
          {list.map((t, i) => <Card key={t.id} t={t} i={i} />)}
        </div>
        <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 md:hidden" data-testid="testimonials-swipe-hint">Geser untuk lihat lainnya →</p>
      </div>
    </section>
  );
};
